'use client';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  createInvite,
  buildInviteUrl,
  sendInviteDelivery,
} from '@/services/inviteService';
import {
  XMarkIcon,
  ClipboardDocumentIcon,
  ShareIcon,
  EnvelopeIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  UserPlusIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/**
 * Per-player parent invite modal — state-aware.
 *
 * Modes (driven by the existing invite passed in via `existingInvite`
 * + the player's email field):
 *
 *   • NO INVITE + has email
 *     The roster's quick-invite handler already fires server-side
 *     email and bypasses this modal entirely. We render this mode
 *     only as a fallback (delivery failed, etc.) — shows the link
 *     for manual share.
 *
 *   • NO INVITE + no email
 *     "Copy link only" view. Coach can grab the URL and share it
 *     manually (text, paper, in person). We don't auto-send because
 *     there's no destination.
 *
 *   • PENDING (status === 'active')
 *     Status header showing when it was sent + the recipient.
 *     Primary action: Re-send the SAME invite via SendGrid (no
 *     duplicate invite created). Secondary: copy link, native share.
 *
 *   • JOINED (status === 'accepted')
 *     Confirmation that the parent joined. Primary action: Invite
 *     another guardian (co-parent, grandparent) — opens an inline
 *     form for the new guardian's email + creates a fresh invite
 *     stamped as guardianContext='additional'.
 *
 * Why state-aware? The previous version auto-created a brand-new
 * invite on every open, which left multiple active invites floating
 * around for the same player. It also meant the coach couldn't see
 * whether the parent had joined yet without leaving the modal.
 */
export default function InviteParentForPlayerModal({
  open,
  onClose,
  teamId,
  teamName,
  player,
  uid,
  existingInvite, // { id, code, email, status, createdAt, ... } from playerInviteByPid
}) {
  const { t } = useTranslation();
  const playerName = `${player?.firstName || ''} ${player?.lastName || ''}`.trim();
  const primaryEmail =
    player?.guardians?.[0]?.email ||
    player?.parentEmail ||
    '';
  const primaryPhone =
    player?.guardians?.[0]?.phone ||
    player?.parentPhone ||
    '';

  // Determine mode from existing invite + email availability.
  const status = String(existingInvite?.status || '').toLowerCase();
  const mode =
    status === 'accepted'
      ? 'joined'
      : status === 'active'
        ? 'pending'
        : primaryEmail
          ? 'no-invite-with-email'
          : 'no-invite-no-email';

  // Fallback invite for the no-invite paths. We only auto-create
  // when the modal opens in 'no-invite-no-email' mode (so coach can
  // copy a link immediately) or as a manual-fallback in the with-
  // email mode if they explicitly ask. We do NOT auto-create on
  // every open like the old version did.
  const [fallbackInvite, setFallbackInvite] = useState(null);
  const [creatingFallback, setCreatingFallback] = useState(false);

  // For the "joined" path, allow inviting an additional guardian.
  // State here is local to the modal (the new contact's email) +
  // sending flag so the button shows "Sending…".
  const [addAnother, setAddAnother] = useState({ open: false, email: '', sending: false });

  // For the pending path, track whether we just re-sent so the
  // button can show "Re-sent" feedback.
  const [resending, setResending] = useState(false);

  // Reset everything when the modal closes so reopening for a
  // different player or in a different state starts fresh.
  useEffect(() => {
    if (!open) {
      setFallbackInvite(null);
      setCreatingFallback(false);
      setAddAnother({ open: false, email: '', sending: false });
      setResending(false);
    }
  }, [open]);

  // Eager-create the link only in the "no email on file" path —
  // we have nothing to send to, so the only useful UI is a link
  // the coach can copy and share manually.
  useEffect(() => {
    if (!open) return;
    if (mode !== 'no-invite-no-email') return;
    if (fallbackInvite) return;
    if (!teamId || !uid || !player) return;

    let cancelled = false;
    (async () => {
      setCreatingFallback(true);
      try {
        const { code } = await createInvite({
          teamId,
          teamName: teamName || null,
          createdBy: uid,
          audience: 'parent',
          preset: 'parent',
          expiresInDays: 30,
          label: `Parent invite — ${playerName || 'player'}`,
          language: player?.guardians?.[0]?.language || player?.language || 'en',
          metadata: {
            createdVia: 'roster-per-player-no-email',
            playerName: playerName || null,
            playerId: player?.id || null,
          },
        });
        if (!cancelled) setFallbackInvite({ code, teamId });
      } catch (err) {
        if (!cancelled) toast.error(err?.message || 'Failed to create invite link');
      } finally {
        if (!cancelled) setCreatingFallback(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, player?.id]);

  if (!open) return null;

  // Pick the right invite code/url for the active mode.
  const activeCode =
    existingInvite?.code ||
    existingInvite?.id ||
    fallbackInvite?.code ||
    null;
  const url = activeCode ? buildInviteUrl(activeCode, teamId) : '';

  const formatTs = (ts) => {
    try {
      const ms = ts?.toMillis?.() ?? (typeof ts === 'number' ? ts : null);
      if (!ms) return null;
      return new Date(ms).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return null;
    }
  };
  const sentAt = formatTs(existingInvite?.lastSentAt || existingInvite?.createdAt);
  const acceptedAt = formatTs(existingInvite?.acceptedAt);

  const shareText = playerName && teamName
    ? t('inviteParent.share.text', { name: playerName, team: teamName, url })
    : teamName
      ? t('inviteParent.share.textNoName', { team: teamName, url })
      : t('inviteParent.share.textGeneric', { url });

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('inviteParent.toast.copied'));
    } catch {
      toast.error(t('inviteParent.toast.copyFailed'));
    }
  };

  const handleNativeShare = async () => {
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: teamName ? t('inviteParent.share.title', { team: teamName }) : t('inviteParent.share.titleGeneric'),
          text: shareText,
          url,
        });
      } catch { /* user cancelled — silent */ }
    } else {
      handleCopy();
    }
  };

  // Pending mode: re-send the SAME invite via SendGrid. Doesn't
  // create a new invite record, just re-fires delivery on the
  // existing one. This was the big bug we're fixing — the old
  // modal generated a duplicate every open.
  const handleResendEmail = async () => {
    if (!url || !primaryEmail) return;
    setResending(true);
    const toastId = toast.loading(t('inviteParent.toast.resending', { email: primaryEmail }));
    try {
      await sendInviteDelivery({
        method: 'email',
        to: primaryEmail,
        inviteUrl: url,
        // Override the (possibly older) invite's stored language with the
        // guardian's current language so a re-send lands in the right one.
        language: player?.guardians?.[0]?.language || player?.language || 'en',
      });
      toast.success(t('inviteParent.toast.resent', { email: primaryEmail }), { id: toastId });
    } catch (err) {
      console.error('[InviteParentForPlayerModal] resend failed', err);
      toast.error(t('inviteParent.toast.resendFailed'), { id: toastId });
    } finally {
      setResending(false);
    }
  };

  // Joined mode: invite another guardian. Creates a brand-new
  // invite stamped with guardianContext='additional' so we can tell
  // it apart from the primary later, and fires server-side email.
  const handleInviteAnother = async () => {
    const email = String(addAnother.email || '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t('inviteParent.toast.invalidEmail'));
      return;
    }
    setAddAnother((p) => ({ ...p, sending: true }));
    const toastId = toast.loading(t('inviteParent.toast.sending', { email }));
    try {
      const { code } = await createInvite({
        teamId,
        teamName: teamName || null,
        createdBy: uid,
        audience: 'parent',
        preset: 'parent',
        email,
        expiresInDays: 30,
        label: `Additional guardian — ${playerName || 'player'}`,
        language: player?.guardians?.[0]?.language || player?.language || 'en',
        metadata: {
          createdVia: 'roster-additional-guardian',
          playerName: playerName || null,
          playerId: player?.id || null,
          guardianContext: 'additional',
        },
      });
      await sendInviteDelivery({
        method: 'email',
        to: email,
        inviteUrl: buildInviteUrl(code, teamId),
      });
      toast.success(t('inviteParent.toast.sent', { email }), { id: toastId });
      setAddAnother({ open: false, email: '', sending: false });
    } catch (err) {
      console.error('[InviteParentForPlayerModal] add-another failed', err);
      toast.error(t('inviteParent.toast.sendFailed'), { id: toastId });
      setAddAnother((p) => ({ ...p, sending: false }));
    }
  };

  // ── Render ──────────────────────────────────────────────────────
  // Header copy adapts per mode so the coach instantly knows which
  // state they're in. Body content branches further down.
  //
  // Plain function (not useMemo) deliberately — has to run AFTER the
  // early-return-on-!open guard, and React hooks must always be
  // called in the same order. Cheap enough not to need memoization.
  const buildHeader = () => {
    if (mode === 'joined') {
      return {
        Icon: CheckCircleIcon,
        iconBg: 'bg-emerald-100',
        iconFg: 'text-emerald-600',
        title: t('inviteParent.header.joinedTitle', { name: playerName || t('inviteParent.playerCap') }),
        subtitle: acceptedAt
          ? t('inviteParent.header.joinedSubAccepted', { date: acceptedAt })
          : t('inviteParent.header.joinedSubDefault'),
      };
    }
    if (mode === 'pending') {
      return {
        Icon: ClockIcon,
        iconBg: 'bg-amber-100',
        iconFg: 'text-amber-600',
        title: t('inviteParent.header.pendingTitle', { name: playerName || t('inviteParent.playerFallback') }),
        subtitle: sentAt
          ? t('inviteParent.header.pendingSubSent', { date: sentAt })
          : t('inviteParent.header.pendingSubDefault'),
      };
    }
    return {
      Icon: UserPlusIcon,
      iconBg: 'bg-[#b5f03d]/25',
      iconFg: 'text-[#3d6010]',
      title: playerName
        ? t('inviteParent.header.inviteTitle', { name: playerName })
        : t('inviteParent.header.inviteTitleNoName'),
      subtitle: mode === 'no-invite-no-email'
        ? t('inviteParent.header.subNoEmail')
        : t('inviteParent.header.subDefault'),
    };
  };
  const HEADER = buildHeader();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/55 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal — flex column capped at 90vh so the footer (Done / Cancel)
          stays pinned and reachable; the body scrolls when content is
          taller than the screen. Without this, tall states (pending
          re-send, joined add-guardian) overflowed and clipped the footer
          on small screens, making the button unclickable. */}
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="shrink-0 flex items-start justify-between px-6 py-4 border-b border-zinc-100 bg-gradient-to-br from-[#b5f03d]/10 to-transparent">
          <div className="flex items-start gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${HEADER.iconBg}`}>
              <HEADER.Icon className={`h-5 w-5 ${HEADER.iconFg}`} />
            </div>
            <div>
              <h2
                className="uppercase text-[#0d1f14] leading-tight"
                style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 20, letterSpacing: '0.5px' }}
              >
                {HEADER.title}
              </h2>
              <p className="text-[12.5px] text-zinc-600 mt-0.5">
                {HEADER.subtitle}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 -m-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body — branches by mode. Scrolls within the capped modal. */}
        <div className="p-6 flex-1 min-h-0 overflow-y-auto">
          {/* JOINED ─────────────────────────────────────────────── */}
          {mode === 'joined' && (
            <>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-sm text-emerald-900 leading-relaxed">
                  {t('inviteParent.joined.body', {
                    who: existingInvite?.acceptedBy?.displayName || existingInvite?.email || t('inviteParent.joined.whoFallback'),
                    name: playerName || t('inviteParent.playerFallback'),
                  })}
                  {acceptedAt && t('inviteParent.joined.acceptedSuffix', { date: acceptedAt })}
                </p>
              </div>

              {/* Add-another inline form */}
              <div className="mt-5 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                <p
                  className="text-[11px] font-bold uppercase tracking-[1.2px] text-zinc-500 mb-2"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {t('inviteParent.joined.addGuardianLabel')}
                </p>
                <p className="text-xs text-zinc-600 mb-3 leading-relaxed">
                  {t('inviteParent.joined.addGuardianDesc', { name: playerName || t('inviteParent.playerFallback') })}
                </p>
                {addAnother.open ? (
                  <div className="space-y-2">
                    <input
                      type="email"
                      autoFocus
                      placeholder={t('inviteParent.joined.emailPlaceholder')}
                      value={addAnother.email}
                      onChange={(e) => setAddAnother((p) => ({ ...p, email: e.target.value }))}
                      disabled={addAnother.sending}
                      className="w-full min-h-[44px] rounded-[10px] border border-zinc-300 bg-white px-3 py-2 text-sm text-[#0d1f14] focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 focus:outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleInviteAnother}
                        disabled={addAnother.sending || !addAnother.email}
                        className="flex-1 inline-flex items-center justify-center rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 min-h-[40px] text-sm font-bold uppercase disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                      >
                        {addAnother.sending ? t('inviteParent.joined.sending') : t('inviteParent.joined.sendInvite')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setAddAnother({ open: false, email: '', sending: false })}
                        disabled={addAnother.sending}
                        className="rounded-[10px] px-3 min-h-[40px] text-sm text-zinc-600 hover:bg-zinc-100 transition-colors"
                      >
                        {t('inviteParent.joined.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setAddAnother({ open: true, email: '', sending: false })}
                    className="inline-flex items-center gap-2 rounded-[10px] border-2 border-[#b5f03d] bg-white text-[#0d1f14] px-4 min-h-[40px] text-sm font-bold uppercase hover:bg-[#b5f03d]/10 transition-colors"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    <UserPlusIcon className="h-4 w-4" />
                    {t('inviteParent.joined.addAnother')}
                  </button>
                )}
              </div>
            </>
          )}

          {/* PENDING ────────────────────────────────────────────── */}
          {mode === 'pending' && (
            <>
              {/* Status callout */}
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm text-amber-900 leading-relaxed">
                  {t('inviteParent.pending.sentToPrefix')}
                  <strong className="font-semibold">{existingInvite?.email || primaryEmail}</strong>
                  {sentAt && t('inviteParent.pending.onDate', { date: sentAt })}
                  {t('inviteParent.pending.sentToSuffix')}
                </p>
              </div>

              {/* Primary: re-send. Secondary: copy / share. */}
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={handleResendEmail}
                  disabled={resending || !primaryEmail}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 min-h-[48px] uppercase disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 14, letterSpacing: '0.6px' }}
                >
                  <ArrowPathIcon className={`h-4 w-4 ${resending ? 'animate-spin' : ''}`} />
                  {resending ? t('inviteParent.pending.resending') : t('inviteParent.pending.resend')}
                </button>
              </div>

              {/* Link box for manual share fallback */}
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  readOnly
                  value={url}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-h-[44px] rounded-[10px] border border-zinc-300 bg-white px-4 py-3 text-[13px] text-[#0d1f14] focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center justify-center gap-2 rounded-[10px] border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#b5f03d]/40 px-4 min-h-[44px] text-sm font-bold text-[#0d1f14] uppercase transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  {t('inviteParent.pending.copyLink')}
                </button>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleNativeShare}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#b5f03d]/40 px-3 py-2 text-[13px] font-semibold text-[#0d1f14] transition-colors"
                >
                  <ShareIcon className="h-4 w-4" />
                  {t('inviteParent.pending.share')}
                </button>
                {primaryPhone && (
                  <a
                    href={`sms:${primaryPhone}?&body=${encodeURIComponent(shareText)}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#b5f03d]/40 px-3 py-2 text-[13px] font-semibold text-[#0d1f14] transition-colors"
                  >
                    <ChatBubbleOvalLeftEllipsisIcon className="h-4 w-4" />
                    {t('inviteParent.pending.sms', { phone: primaryPhone })}
                  </a>
                )}
              </div>
            </>
          )}

          {/* NO INVITE — no email on file ─────────────────────── */}
          {mode === 'no-invite-no-email' && (
            <>
              {creatingFallback || !fallbackInvite ? (
                <div className="space-y-3">
                  <div className="h-12 rounded-[10px] bg-zinc-100 animate-pulse" />
                  <p className="text-[12.5px] text-zinc-400 mt-2">{t('inviteParent.noEmail.generating')}</p>
                </div>
              ) : (
                <>
                  <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                    <p className="text-sm text-zinc-700 leading-relaxed">
                      {t('inviteParent.noEmail.body', { name: playerName || t('inviteParent.playerFallback') })}
                    </p>
                  </div>

                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      readOnly
                      value={url}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 min-h-[44px] rounded-[10px] border border-zinc-300 bg-white px-4 py-3 text-[13px] text-[#0d1f14] focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 min-h-[44px] text-sm font-bold uppercase transition-colors"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      {t('inviteParent.noEmail.copyLink')}
                    </button>
                  </div>

                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={handleNativeShare}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#b5f03d]/40 px-3 py-2 text-[13px] font-semibold text-[#0d1f14] transition-colors"
                    >
                      <ShareIcon className="h-4 w-4" />
                      {t('inviteParent.noEmail.shareVia')}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* NO INVITE — has email (rare fallback) ─────────────── */}
          {mode === 'no-invite-with-email' && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-900 leading-relaxed">
                {t('inviteParent.fallback.body')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 flex justify-end gap-2 px-6 py-3.5 border-t border-zinc-100 bg-zinc-50">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] px-4 min-h-[40px] text-[13px] font-semibold text-zinc-700 hover:bg-zinc-200 transition-colors"
          >
            {t('inviteParent.done')}
          </button>
        </div>
      </div>
    </div>
  );
}
